/*
 * Copyright (c) Nexign, JSC, 1992-2022.
 */
/**
 * The personal data delete modal window.
 *
 * @author Dmitriy Kvon
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.17.0
 */

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { makeSelectAuthoritiesIsServiceUser } from '@/main/pages/PageAuth/selectors/authSelectors';
import { IRootReducer } from '@/main/reducers/rootReducer';
import { Button, LinkBlock } from '@components/common';
import { ImgStatusError } from '@components/common/Block/Img';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';

import withAsyncStateManagement from '../../asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '../../internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '../../internationalization/constants';
import {
    makeSelectPersonalDataDeleteTranslation,
    makeSelectTranslationCommon,
} from '../../internationalization/intSelectors';
import { ICommonTranslation, IPersonalDataDeleteTranslation } from '../../internationalization/types';
import Modal from '../../modal/components/Modal';
import ModalOrder from '../../modal/components/ModalOrder';
import { ModalCenterStyled } from '../../modal/components/styled/ModalCenterStyled';
import { makeSelectorModalStatus } from '../../modal/modalSelectors';
import { EModalStatus, IModalRenderProps } from '../../modal/types/modalTypes';
import { personalDataDeleteStartAction } from '../personalDataDeleteActions';
import personalDataDeleteReducer, { IPersonalDataDeleteReducer } from '../personalDataDeleteReducer';
import { personalDataDeleteSaga } from '../personalDataDeleteSaga';
import { makeSelectPersonalDataDeleteOrderId } from '../personalDataDeleteSelectors';
import { PersonalDataDeleteModalStyled } from './styled/PersonalDataDeleteModalStyled';

type IPersonalDataDeleteModal = Pick<IModalRenderProps, 'onCloseModal'> & IDispatchProps & IConnectedProps;

const PersonalDataDeleteModal = ({
    deletePersonalData,
    onCloseModal,
    orderId,
    translationCommon,
    translationPersonalDataDelete,
    isServiceUser,
    status,
}: IPersonalDataDeleteModal) => {

    return (
        <>
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.personalDataDelete]} />

            <Modal dataName="personal-data-delete-modal"
                width="580px"
                withoutPadding={status === EModalStatus.start}>
                <PersonalDataDeleteModalStyled>
                    <Modal.Status status={EModalStatus.start}>
                        <Modal.Title className="delete-modal__title">
                            {translationPersonalDataDelete.status[EModalStatus.start].title}
                        </Modal.Title>
                        <Modal.Body highlightBg className="delete-modal__body">
                            <Modal.Text>
                                <div dangerouslySetInnerHTML={
                                    { __html: translationPersonalDataDelete.status[EModalStatus.start].text }
                                } />
                            </Modal.Text>
                        </Modal.Body>
                        <Modal.Control>
                            <ArrowTooltip disableHoverListener={!isServiceUser}
                                message={translationCommon.serviceUser.tooltip}
                                placement="top"
                            >
                                <span>
                                    <Button dataName="subs-personal-data-delete-modal-btn-agree"
                                        disabled={isServiceUser}
                                        kind="medium"
                                        onClick={deletePersonalData}>
                                        {translationCommon.modal.remove}
                                    </Button>
                                </span>
                            </ArrowTooltip>
                            <LinkBlock
                                dataName="subs-personal-data-delete-modal-btn-cancel"
                                onClick={onCloseModal}>
                                {translationCommon.modal.cancel}
                            </LinkBlock>
                        </Modal.Control>
                    </Modal.Status>

                    <Modal.Status status={EModalStatus.success}>
                        <ModalOrder
                            orderId={orderId}
                            orderName={translationPersonalDataDelete.status[EModalStatus.success].orderName}
                            onCloseModal={onCloseModal}
                        />
                    </Modal.Status>

                    <Modal.Status status={EModalStatus.failure}>
                        <ModalCenterStyled>
                            <ImgStatusError />
                            <Modal.Title>
                                {translationPersonalDataDelete.status[EModalStatus.failure].title}
                            </Modal.Title>
                            <Modal.Text className="delete-modal__failure-text">
                                {translationPersonalDataDelete.status[EModalStatus.failure].text}
                            </Modal.Text>
                            <Modal.Control>
                                <Button
                                    dataName="subs-personal-data-delete-modal-btn-accessibly"
                                    onClick={onCloseModal}>
                                    {translationCommon.modal.accessibly}
                                </Button>
                            </Modal.Control>
                        </ModalCenterStyled>
                    </Modal.Status>
                </PersonalDataDeleteModalStyled>
            </Modal>
        </>
    );
};

interface IDispatchProps {
    deletePersonalData: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    deletePersonalData: () => dispatch(personalDataDeleteStartAction()),
});

interface IConnectedProps extends Pick<IPersonalDataDeleteReducer, 'orderId'> {
    translationCommon: ICommonTranslation;
    translationPersonalDataDelete: IPersonalDataDeleteTranslation;
    isServiceUser: boolean;
    status: string;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    orderId: makeSelectPersonalDataDeleteOrderId(),
    translationCommon: makeSelectTranslationCommon(),
    translationPersonalDataDelete: makeSelectPersonalDataDeleteTranslation(),
    isServiceUser: makeSelectAuthoritiesIsServiceUser(),
    status: makeSelectorModalStatus(),
});

// withAsyncStateManagement connects for the lifetime of the modal window
export default withAsyncStateManagement(
    connect(mapStateToProps, mapDispatchToProps)(PersonalDataDeleteModal),
    { reducer: { personalDataDelete: personalDataDeleteReducer }, sagas: personalDataDeleteSaga },
);
